/**
 * <collection-banner-tiles> — the subcollection tiles rail in sections/main-collection-banner.liquid.
 * Tiles render as a static row; Swiper is only created while they no longer fit the rail
 * (and destroyed again once they do). Gap and edge offset come from the CSS custom properties
 * --cb-tile-spacing / --cb-gutter so the static row and the slider always line up.
 *  - keyboard reachability: focusing a tile that sits off the track slides it into view
 *  - no hover zoom while dragging, no slide animation under reduced motion
 *  - back/forward cache: restored pages start with the rail unshifted
 */
if (!customElements.get('collection-banner-tiles')) {
  class CollectionBannerTiles extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.collection-banner__swiper');
      this.wrapper = this.rail?.querySelector('.swiper-wrapper');
      if (!this.wrapper) return;

      this.onFocusIn = this.handleFocusIn.bind(this);
      this.onPageShow = this.handlePageShow.bind(this);
      this.onDragStart = () => this.classList.add('is-dragging');
      this.onDragEnd = () => this.classList.remove('is-dragging');

      this.rail.addEventListener('focusin', this.onFocusIn);
      window.addEventListener('pageshow', this.onPageShow);
      this.resizeObserver = new ResizeObserver(() => this.sync());
      this.resizeObserver.observe(this.rail);
      this.sync();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.rail?.removeEventListener('focusin', this.onFocusIn);
      window.removeEventListener('pageshow', this.onPageShow);
      this.destroySwiper();
    }

    get spacing() {
      const style = getComputedStyle(this);
      return {
        gap: parseFloat(style.getPropertyValue('--cb-tile-spacing')) || 0,
        offset: parseFloat(style.getPropertyValue('--cb-gutter')) || 0,
      };
    }

    fits({ gap, offset }) {
      const slides = [...this.wrapper.children];
      const needed = slides.reduce((total, slide) => total + slide.offsetWidth, 0) + gap * (slides.length - 1) + offset * 2;
      const { paddingLeft, paddingRight } = getComputedStyle(this.rail);
      const available = this.rail.clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight);
      return needed <= available + 1;
    }

    sync() {
      const spacing = this.spacing;
      if (this.fits(spacing)) {
        this.destroySwiper();
        return;
      }
      const { params } = this.swiper ?? {};
      if (params?.spaceBetween === spacing.gap && params?.slidesOffsetBefore === spacing.offset) return;
      this.destroySwiper();
      this.initSwiper(spacing);
    }

    initSwiper({ gap, offset }) {
      if (typeof Swiper === 'undefined') return;
      this.swiper = new Swiper(this.rail, {
        slidesPerView: 'auto',
        spaceBetween: gap,
        slidesOffsetBefore: offset,
        slidesOffsetAfter: offset,
        watchOverflow: true,
        resistanceRatio: 0.68,
        speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300,
      });
      this.swiper.on('sliderFirstMove', this.onDragStart);
      this.swiper.on('touchEnd', this.onDragEnd);

      const activeIndex = [...this.swiper.slides].findIndex((slide) => slide.querySelector('[aria-current]'));
      if (activeIndex > 0) this.swiper.slideTo(activeIndex, 0);
    }

    destroySwiper() {
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
      this.classList.remove('is-dragging');
    }

    resetScroll() {
      this.rail.scrollLeft = 0;
      requestAnimationFrame(() => {
        this.rail.scrollLeft = 0;
      });
    }

    handlePageShow({ persisted }) {
      if (!persisted) return;
      this.resetScroll();
      this.sync();
      this.swiper?.update();
    }

    // Browsers scroll an overflow:hidden container to reveal focus; Swiper moves by transform.
    handleFocusIn({ target }) {
      this.resetScroll();
      const slide = target.closest('.swiper-slide');
      if (!slide || !this.swiper) return;
      const railRect = this.rail.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const fullyVisible = slideRect.left >= railRect.left - 1 && slideRect.right <= railRect.right + 1;
      if (!fullyVisible) this.swiper.slideTo(this.swiper.slides.indexOf(slide));
    }
  }

  customElements.define('collection-banner-tiles', CollectionBannerTiles);
}
